/**
 * Physical MIDI keyboard size → displayed MIDI range.
 * Matches common controller footprints so on-screen keys line up 1:1 with hardware.
 */

export type KeyboardSizeId = 25 | 37 | 49 | 61 | 76 | 88;

export interface KeyboardRange {
  size: KeyboardSizeId;
  /** Inclusive MIDI lows/highs (standard controller layouts). */
  lowMidi: number;
  highMidi: number;
  label: string;
  /** Approximate white-key count (for UI). */
  whiteKeys: number;
}

/** Canonical ranges used by most MIDI controllers. */
export const KEYBOARD_RANGES: Record<KeyboardSizeId, KeyboardRange> = {
  25: {
    size: 25,
    lowMidi: 48,
    highMidi: 72,
    label: "25-key · C3–C5",
    whiteKeys: 15,
  },
  37: {
    size: 37,
    lowMidi: 48,
    highMidi: 84,
    label: "37-key · C3–C6",
    whiteKeys: 22,
  },
  49: {
    size: 49,
    lowMidi: 36,
    highMidi: 84,
    label: "49-key · C2–C6",
    whiteKeys: 29,
  },
  61: {
    size: 61,
    lowMidi: 36,
    highMidi: 96,
    label: "61-key · C2–C7",
    whiteKeys: 36,
  },
  76: {
    size: 76,
    lowMidi: 28,
    highMidi: 103,
    label: "76-key · E1–G7",
    whiteKeys: 45,
  },
  88: {
    size: 88,
    lowMidi: 21,
    highMidi: 108,
    label: "88-key · A0–C8",
    whiteKeys: 52,
  },
};

export const KEYBOARD_SIZE_OPTIONS: KeyboardSizeId[] = [25, 37, 49, 61, 76, 88];

export function getKeyboardRange(size: KeyboardSizeId): KeyboardRange {
  return KEYBOARD_RANGES[size];
}

/** Guess from USB/BT product name ("Launchkey 49", "Keystation 61", …). */
export function guessKeyboardSizeFromDeviceName(name: string): KeyboardSizeId | null {
  const n = name.toLowerCase();
  if (/\b88\b|full\s*size|weighted|stage\s*piano|fp-?30|p-?125|rdx?\s*88/.test(n)) return 88;
  if (/\b76\b/.test(n)) return 76;
  if (/\b61\b/.test(n)) return 61;
  if (/\b49\b/.test(n)) return 49;
  if (/\b37\b/.test(n)) return 37;
  if (/\b25\b|mini\s*key|oxygen\s*25|launchkey\s*mini/.test(n)) return 25;
  return null;
}

/**
 * Pick the smallest standard keyboard that fully covers [minMidi, maxMidi].
 */
export function snapObservedRangeToKeyboard(minMidi: number, maxMidi: number): KeyboardSizeId {
  for (const size of KEYBOARD_SIZE_OPTIONS) {
    const r = KEYBOARD_RANGES[size];
    if (minMidi >= r.lowMidi - 1 && maxMidi <= r.highMidi + 1) return size;
  }
  return 88;
}

/** Expand a base range so practice/exam guide notes stay visible. */
export function expandRangeToCover(
  base: KeyboardRange,
  midis: number[],
): { lowMidi: number; highMidi: number } {
  let low = base.lowMidi;
  let high = base.highMidi;
  for (const m of midis) {
    if (!Number.isFinite(m)) continue;
    low = Math.min(low, m);
    high = Math.max(high, m);
  }
  // Snap outward to white-key boundaries when possible
  while (low > 0 && ![0, 2, 4, 5, 7, 9, 11].includes(low % 12)) low -= 1;
  while (high < 127 && ![0, 2, 4, 5, 7, 9, 11].includes(high % 12)) high += 1;
  return { lowMidi: Math.max(21, low), highMidi: Math.min(108, high) };
}
